/**
 * Responsibility: the colours a slab tile is framed and labelled in.
 *
 * Its own file rather than an export beside the tile component, so the
 * component file exports only components and fast refresh keeps working.
 *
 * **The frame is keyed to the grade, not the grader.** A wall of tiles is
 * scanned for the tens, and grade is what the eye is looking for; the
 * company is a detail read afterwards, so it lives in the label text. The
 * ramp is metallic on purpose - gold, silver, bronze, then slate - because
 * that is the vocabulary the hobby already uses for ranks and it needs no
 * legend.
 *
 * Each grade carries a pair: a solid edge and a lighter highlight. Together
 * they make the sheen on the frame, which is a gradient between them rather
 * than an image, so it costs nothing and follows the theme.
 */

export type GradeSkin = {
  /** The frame's base colour. */
  edge: string;
  /** The highlight the sheen sweeps through. */
  sheen: string;
  /** Text on the label, chosen for contrast against `edge`. */
  ink: string;
};

/** Gold at the top, then silver, bronze and slate. */
const GEM: GradeSkin = { edge: '#b8912f', sheen: '#f2d98a', ink: '#1a1405' };
const NEAR_GEM: GradeSkin = { edge: '#7f8c9b', sheen: '#dce6ef', ink: '#10161c' };
const HIGH: GradeSkin = { edge: '#6b7785', sheen: '#c3ced9', ink: '#0d1217' };
const MID: GradeSkin = { edge: '#8a6a45', sheen: '#d8b98c', ink: '#1a1208' };
const LOW: GradeSkin = { edge: '#4a5058', sheen: '#9aa3ad', ink: '#f2f4f6' };

/** A slab whose title never said a number. Still a slab. */
const UNKNOWN: GradeSkin = { edge: '#5b6470', sheen: '#a9b3bf', ink: '#f2f4f6' };

/** The graders offered when recording a holding. */
export const GRADING_COMPANIES = ['PSA', 'BGS', 'CGC', 'SGC'] as const;

/**
 * The skin for a grade, read from the label so half grades count.
 *
 * "9.5" is nearer a ten than a nine in both price and feel, so it gets its
 * own tier rather than rounding down into the nines.
 */
export function gradeSkin(gradeLabel: string | null | undefined): GradeSkin {
  if (!gradeLabel) return UNKNOWN;
  const value = Number.parseFloat(gradeLabel);
  if (!Number.isFinite(value)) return UNKNOWN;
  if (value >= 10) return GEM;
  if (value >= 9.5) return NEAR_GEM;
  if (value >= 9) return HIGH;
  if (value >= 7) return MID;
  return LOW;
}

/**
 * A frame that reads as foil without being a picture of foil.
 *
 * Three stops rather than two: the highlight sits off-centre so the sweep
 * looks lit from one corner, which is what makes a flat gradient read as a
 * surface rather than a stripe.
 */
export function sheenGradient(skin: GradeSkin): string {
  return (
    `linear-gradient(135deg, ${skin.edge} 0%, ${skin.sheen} 38%, ` +
    `${skin.edge} 62%, ${skin.edge} 100%)`
  );
}
